import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import type { CoachRow } from "./coaches.types";

function randomToken() {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export const listCoaches = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await supabaseAdmin
    .from("coaches")
    .select("*")
    .order("full_name", { ascending: true });
  if (error) throw new Error(error.message);
  return { coaches: (data ?? []) as CoachRow[] };
});

export const getCoach = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const { data: row, error } = await supabaseAdmin
      .from("coaches")
      .select("*")
      .eq("id", data.id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return { coach: (row as CoachRow) ?? null };
  });

const createCoachSchema = z.object({
  full_name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(255),
});

export const createCoach = createServerFn({ method: "POST" })
  .inputValidator((d) => createCoachSchema.parse(d))
  .handler(async ({ data }) => {
    // Find or insert
    const { data: existing } = await supabaseAdmin
      .from("coaches")
      .select("id")
      .eq("email", data.email.toLowerCase())
      .maybeSingle();

    let coachId = existing?.id;
    if (!coachId) {
      const { data: inserted, error } = await supabaseAdmin
        .from("coaches")
        .insert({ full_name: data.full_name, email: data.email.toLowerCase() })
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      coachId = inserted.id;
    }

    const token = randomToken();
    const { error: invErr } = await supabaseAdmin
      .from("coach_invites")
      .insert({ coach_id: coachId, token, purpose: "initial" });
    if (invErr) throw new Error(invErr.message);

    return { coachId, token };
  });

export const requestProfileUpdate = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ coachId: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const token = randomToken();
    const { error } = await supabaseAdmin
      .from("coach_invites")
      .insert({ coach_id: data.coachId, token, purpose: "update" });
    if (error) throw new Error(error.message);
    await supabaseAdmin
      .from("coaches")
      .update({ last_update_requested_at: new Date().toISOString() })
      .eq("id", data.coachId);
    return { token };
  });

export const updateEngagements = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z.object({
      coachId: z.string().uuid(),
      active: z.boolean(),
      engagements_count: z.number().int().min(0).max(999),
    }).parse(d),
  )
  .handler(async ({ data }) => {
    const { error } = await supabaseAdmin
      .from("coaches")
      .update({ active: data.active, engagements_count: data.engagements_count })
      .eq("id", data.coachId);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

// ----- public form (token-based) -----
export const getCoachByToken = createServerFn({ method: "GET" })
  .inputValidator((d) => z.object({ token: z.string().min(10).max(128) }).parse(d))
  .handler(async ({ data }) => {
    const { data: invite, error } = await supabaseAdmin
      .from("coach_invites")
      .select("coach_id, used_at, expires_at, purpose")
      .eq("token", data.token)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!invite) return { ok: false as const, reason: "not_found" as const };
    if (new Date(invite.expires_at) < new Date())
      return { ok: false as const, reason: "expired" as const };

    const { data: coach } = await supabaseAdmin
      .from("coaches")
      .select("*")
      .eq("id", invite.coach_id)
      .single();
    return { ok: true as const, coach: coach as CoachRow, purpose: invite.purpose };
  });

const profileSchema = z.object({
  token: z.string().min(10).max(128),
  full_name: z.string().trim().min(1).max(120),
  phone: z.string().trim().max(40).optional().nullable(),
  city: z.string().trim().max(80).optional().nullable(),
  languages: z.array(z.string().trim().min(1).max(40)).max(10),
  linkedin_url: z.string().trim().max(255).optional().nullable(),
  bio: z.string().trim().max(2000).optional().nullable(),
  previous_companies: z.string().trim().max(2000).optional().nullable(),
  industries: z.array(z.string().min(1).max(80)).max(20),
  functions: z.array(z.string().min(1).max(80)).max(20),
  business_phases: z.array(z.string().min(1).max(80)).max(20),
  // Optional photo as base64 data URL
  photo_data_url: z.string().max(8_000_000).optional().nullable(),
});

export const submitCoachProfile = createServerFn({ method: "POST" })
  .inputValidator((d) => profileSchema.parse(d))
  .handler(async ({ data }) => {
    const { data: invite, error: invErr } = await supabaseAdmin
      .from("coach_invites")
      .select("coach_id, expires_at")
      .eq("token", data.token)
      .maybeSingle();
    if (invErr) throw new Error(invErr.message);
    if (!invite) throw new Error("Länken är ogiltig.");
    if (new Date(invite.expires_at) < new Date()) throw new Error("Länken har gått ut.");

    let photo_url: string | undefined;
    if (data.photo_data_url) {
      const m = data.photo_data_url.match(/^data:(image\/(png|jpe?g|webp));base64,(.+)$/i);
      if (!m) throw new Error("Ogiltigt bildformat. Använd PNG, JPG eller WebP.");
      const mime = m[1];
      const ext = mime === "image/png" ? "png" : mime === "image/webp" ? "webp" : "jpg";
      const bytes = Uint8Array.from(atob(m[3]), (c) => c.charCodeAt(0));
      if (bytes.byteLength > 5 * 1024 * 1024) throw new Error("Bilden är för stor (max 5 MB).");
      const path = `${invite.coach_id}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabaseAdmin.storage
        .from("coach-photos")
        .upload(path, bytes, { contentType: mime, upsert: true });
      if (upErr) throw new Error(upErr.message);
      const { data: pub } = supabaseAdmin.storage.from("coach-photos").getPublicUrl(path);
      photo_url = pub.publicUrl;
    }

    const update: {
      full_name: string;
      phone: string | null;
      city: string | null;
      languages: string[];
      linkedin_url: string | null;
      bio: string | null;
      previous_companies: string | null;
      industries: string[];
      functions: string[];
      business_phases: string[];
      profile_completed_at: string;
      photo_url?: string;
    } = {
      full_name: data.full_name,
      phone: data.phone ?? null,
      city: data.city ?? null,
      languages: data.languages,
      linkedin_url: data.linkedin_url ?? null,
      bio: data.bio ?? null,
      previous_companies: data.previous_companies ?? null,
      industries: data.industries,
      functions: data.functions,
      business_phases: data.business_phases,
      profile_completed_at: new Date().toISOString(),
    };
    if (photo_url) update.photo_url = photo_url;

    const { error: updErr } = await supabaseAdmin
      .from("coaches")
      .update(update)
      .eq("id", invite.coach_id);
    if (updErr) throw new Error(updErr.message);

    await supabaseAdmin
      .from("coach_invites")
      .update({ used_at: new Date().toISOString() })
      .eq("token", data.token);

    return { ok: true };
  });
